import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { ToggleGroup } from '@src/components/common/ToggleGroup';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import styled from 'styled-components';

import {
  ALL_CONTENT_TYPES,
  contentTypeColors,
  contentTypeLabels,
  getClipStatusFilterOptions,
} from './contentItems';

import type {
  ClipStatusFilter,
  ContentOwnerFilter,
  ContentType,
} from './contentItems';
import type { ReturnTypeUseDropdown } from './useDropdown';
import type { DatoSpeaker } from '@src/types.ts/story';

export interface RecentContentFiltersProps {
  activeTypeFilters: Set<ContentType>;
  toggleTypeFilter: (type: ContentType) => void;
  activeStoryFilter: string | null;
  setStoryFilter: (storyId: string | null) => void;
  activeUpdatedByFilter: string | null;
  setUpdatedByFilter: (name: string | null) => void;
  /** Selected speaker's display name (speakers are matched by name). */
  activeSpeakerFilter: string | null;
  setSpeakerFilter: (speakerName: string | null) => void;
  activeStatusFilters: Set<ClipStatusFilter>;
  toggleStatusFilter: (status: ClipStatusFilter) => void;
  /** Gates the review-status options in Clip Status (PLAT-10480 flag). */
  showReviewWorkflow: boolean;
  contentOwner: ContentOwnerFilter;
  setContentOwner: (owner: ContentOwnerFilter) => void;
  myContentCount: number;
  allContentCount: number;
  uniqueStories: Array<[string, string]>;
  uniqueUpdatedBy: string[];
  uniqueSpeakers: DatoSpeaker[];
  /**
   * The signed-in user's editor name. Distinguishes "narrowed to me" (which the
   * My Content segment reports) from "narrowed to a teammate" (which Updated By
   * reports), so the two controls never state the same thing.
   */
  currentEditorName?: string;
  activeFilterCount: number;
  clearAllFilters: () => void;
  searchQuery: string;
  setSearchQueryValue: (value: string) => void;
  typeDropdown: ReturnTypeUseDropdown;
  statusDropdown: ReturnTypeUseDropdown;
  storyDropdown: ReturnTypeUseDropdown;
  updatedByDropdown: ReturnTypeUseDropdown;
  speakerDropdown: ReturnTypeUseDropdown;
}

export function RecentContentFilters({
  activeTypeFilters,
  toggleTypeFilter,
  activeStoryFilter,
  setStoryFilter,
  activeUpdatedByFilter,
  setUpdatedByFilter,
  activeSpeakerFilter,
  setSpeakerFilter,
  activeStatusFilters,
  toggleStatusFilter,
  showReviewWorkflow,
  contentOwner,
  setContentOwner,
  myContentCount,
  allContentCount,
  uniqueStories,
  uniqueUpdatedBy,
  uniqueSpeakers,
  currentEditorName,
  activeFilterCount,
  clearAllFilters,
  searchQuery,
  setSearchQueryValue,
  typeDropdown,
  statusDropdown,
  storyDropdown,
  updatedByDropdown,
  speakerDropdown,
}: RecentContentFiltersProps) {
  // Whether Updated By is narrowing to someone *other* than the signed-in
  // user, which is the only case this dropdown still represents on its own.
  const isTeammateFilter =
    !!activeUpdatedByFilter && activeUpdatedByFilter !== currentEditorName;

  // The owner toggle stays up whenever we know who is signed in, even at zero
  // — unlike the Me row it carries a count, so "My Content 0" answers "is any
  // of this mine?" outright. `currentEditor` yields 'Unknown' for an
  // unresolved user, which would match nothing and mislead.
  const showOwnerToggle =
    !!currentEditorName && currentEditorName !== 'Unknown';

  return (
    <HeaderActions data-testid="home-content-filters">
      <FilterRow>
        {/* Scope, ahead of the narrowing dropdowns. The segments are My /
            All rather than My / Team because an item's owner is its last
            editor, and that is blank on clips predating lastActionJson and on
            quotes/articles saved without a username — a not-mine "Team"
            segment would drop every one of them out of both views. */}
        {showOwnerToggle && (
          <ToggleGroup<ContentOwnerFilter>
            options={[
              // Gold only on My Content: in this row the accent means "a
              // filter is applied", and All Content is the unfiltered resting
              // view — accenting both would leave gold permanently lit and stop
              // it distinguishing a narrowed list from the default one.
              {
                id: 'mine',
                label: 'My Content',
                count: myContentCount,
                activeTone: 'accent',
              },
              { id: 'all', label: 'All Content', count: allContentCount },
            ]}
            value={contentOwner}
            onChange={setContentOwner}
            ariaLabel="Content owner"
            testIdPrefix="home-content-owner"
          />
        )}
        <DropdownWrapper ref={typeDropdown.dropdownRef}>
          <DropdownButton
            $active={activeTypeFilters.size > 0}
            onClick={typeDropdown.toggle}
          >
            <TriggerLabel>
              {activeTypeFilters.size > 0
                ? `Type (${activeTypeFilters.size})`
                : 'Type'}
            </TriggerLabel>
            <ChevronDownIcon strokeColor="var(--accent)" />
          </DropdownButton>
          {typeDropdown.isOpen && (
            <DropdownMenu>
              {ALL_CONTENT_TYPES.map(type => (
                <DropdownCheckItem
                  key={type}
                  onClick={() => toggleTypeFilter(type)}
                >
                  <Checkbox $checked={activeTypeFilters.has(type)}>
                    {activeTypeFilters.has(type) && <CheckIconAlt />}
                  </Checkbox>
                  <DropdownItemLabel $type={type}>
                    {contentTypeLabels[type]}
                  </DropdownItemLabel>
                </DropdownCheckItem>
              ))}
            </DropdownMenu>
          )}
        </DropdownWrapper>
        <DropdownWrapper ref={statusDropdown.dropdownRef}>
          <DropdownButton
            $active={activeStatusFilters.size > 0}
            onClick={statusDropdown.toggle}
          >
            <TriggerLabel>
              {activeStatusFilters.size > 0
                ? `Clip Status (${activeStatusFilters.size})`
                : 'Clip Status'}
            </TriggerLabel>
            <ChevronDownIcon strokeColor="var(--accent)" />
          </DropdownButton>
          {statusDropdown.isOpen && (
            <DropdownMenu>
              {getClipStatusFilterOptions(showReviewWorkflow).map(option => (
                <DropdownCheckItem
                  key={option.value}
                  onClick={() => toggleStatusFilter(option.value)}
                >
                  <Checkbox $checked={activeStatusFilters.has(option.value)}>
                    {activeStatusFilters.has(option.value) && <CheckIconAlt />}
                  </Checkbox>
                  <CheckItemLabel>{option.label}</CheckItemLabel>
                </DropdownCheckItem>
              ))}
            </DropdownMenu>
          )}
        </DropdownWrapper>
        <DropdownWrapper ref={storyDropdown.dropdownRef}>
          <DropdownButton
            $active={!!activeStoryFilter}
            onClick={storyDropdown.toggle}
          >
            <TriggerLabel>
              {activeStoryFilter
                ? (uniqueStories.find(
                    ([id]) => id === activeStoryFilter,
                  )?.[1] ?? 'Story')
                : 'Story'}
            </TriggerLabel>
            <ChevronDownIcon strokeColor="var(--accent)" />
          </DropdownButton>
          {storyDropdown.isOpen && (
            <DropdownMenu style={{ minWidth: '220px' }}>
              <DropdownSearchInput
                type="text"
                placeholder="Search stories..."
                value={storyDropdown.searchQuery}
                onChange={e => storyDropdown.setSearchQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
                autoFocus
              />
              <DropdownSelectItem
                $active={!activeStoryFilter}
                onClick={() => {
                  setStoryFilter(null);
                  storyDropdown.close();
                }}
              >
                All Stories
              </DropdownSelectItem>
              {uniqueStories
                .filter(([, title]) =>
                  title
                    .toLowerCase()
                    .includes(storyDropdown.searchQuery.toLowerCase()),
                )
                .map(([id, title]) => (
                  <DropdownSelectItem
                    key={id}
                    $active={activeStoryFilter === id}
                    onClick={() => {
                      setStoryFilter(id);
                      storyDropdown.close();
                    }}
                  >
                    {title}
                  </DropdownSelectItem>
                ))}
            </DropdownMenu>
          )}
        </DropdownWrapper>
        <DropdownWrapper ref={speakerDropdown.dropdownRef}>
          <DropdownButton
            $active={!!activeSpeakerFilter}
            onClick={speakerDropdown.toggle}
          >
            <TriggerLabel>{activeSpeakerFilter ?? 'Speaker'}</TriggerLabel>
            <ChevronDownIcon strokeColor="var(--accent)" />
          </DropdownButton>
          {speakerDropdown.isOpen && (
            <DropdownMenu style={{ minWidth: '200px' }}>
              <DropdownSearchInput
                type="text"
                placeholder="Search speakers..."
                value={speakerDropdown.searchQuery}
                onChange={e => speakerDropdown.setSearchQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
                autoFocus
              />
              <DropdownSelectItem
                $active={!activeSpeakerFilter}
                onClick={() => {
                  setSpeakerFilter(null);
                  speakerDropdown.close();
                }}
              >
                All Speakers
              </DropdownSelectItem>
              {uniqueSpeakers
                .filter(speaker =>
                  speaker.name
                    .toLowerCase()
                    .includes(speakerDropdown.searchQuery.toLowerCase()),
                )
                .map(speaker => (
                  <AvatarSelectItem
                    key={speaker.name}
                    $active={activeSpeakerFilter === speaker.name}
                    onClick={() => {
                      setSpeakerFilter(speaker.name);
                      speakerDropdown.close();
                    }}
                  >
                    <SpeakerAvatar
                      name={speaker.name}
                      color={getSpeakerColorFromLabel(speaker.id)}
                      headshotUrl={speaker.headshots?.[0]?.url}
                      size={20}
                      initialsLength={2}
                      initialsFontSize="8px"
                    />
                    <AvatarRowName>{speaker.name}</AvatarRowName>
                  </AvatarSelectItem>
                ))}
            </DropdownMenu>
          )}
        </DropdownWrapper>
        <DropdownWrapper ref={updatedByDropdown.dropdownRef}>
          {/* Deliberately blind to "updated by me": the My Content segment
              already reports that, and restating it here both said it twice and
              widened this trigger, sliding the whole right-aligned filter
              cluster sideways on every toggle. Gold and the name are for a
              teammate — a narrowing only this dropdown can express. */}
          <DropdownButton
            $active={isTeammateFilter}
            onClick={updatedByDropdown.toggle}
          >
            <TriggerLabel>
              {isTeammateFilter ? activeUpdatedByFilter : 'Updated By'}
            </TriggerLabel>
            <ChevronDownIcon strokeColor="var(--accent)" />
          </DropdownButton>
          {updatedByDropdown.isOpen && (
            <DropdownMenu style={{ minWidth: '200px' }}>
              <DropdownSearchInput
                type="text"
                placeholder="Search people..."
                value={updatedByDropdown.searchQuery}
                onChange={e => updatedByDropdown.setSearchQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
                autoFocus
              />
              <DropdownSelectItem
                $active={!activeUpdatedByFilter}
                onClick={() => {
                  setUpdatedByFilter(null);
                  updatedByDropdown.close();
                }}
              >
                All People
              </DropdownSelectItem>
              {/* The signed-in user stays in the list: picking their own name
                  is the same state the My Content segment sets, and the two
                  share it, so the segment simply lights up. */}
              {uniqueUpdatedBy
                .filter(name =>
                  name
                    .toLowerCase()
                    .includes(updatedByDropdown.searchQuery.toLowerCase()),
                )
                .map(name => (
                  <AvatarSelectItem
                    key={name}
                    $active={activeUpdatedByFilter === name}
                    onClick={() => {
                      setUpdatedByFilter(name);
                      updatedByDropdown.close();
                    }}
                  >
                    <SpeakerAvatar
                      name={name}
                      color={getSpeakerColorFromLabel(name)}
                      size={20}
                      initialsLength={2}
                      initialsFontSize="8px"
                    />
                    <AvatarRowName>{name}</AvatarRowName>
                  </AvatarSelectItem>
                ))}
            </DropdownMenu>
          )}
        </DropdownWrapper>
        {/* Always mounted, only hidden: this row is right-aligned, so a
            control that comes and goes changes the cluster's width and slides
            every filter sideways on the click that reveals it. `visibility`
            keeps the layout box; `disabled` keeps it off the tab order and
            unclickable while it is invisible. */}
        <ClearFiltersButton
          $visible={activeFilterCount > 0}
          disabled={activeFilterCount === 0}
          aria-hidden={activeFilterCount === 0}
          onClick={() => {
            clearAllFilters();
            typeDropdown.close();
            statusDropdown.close();
            storyDropdown.close();
            updatedByDropdown.close();
            speakerDropdown.close();
          }}
        >
          Clear
        </ClearFiltersButton>
      </FilterRow>
      <SearchInput
        type="text"
        placeholder="Search content..."
        value={searchQuery}
        onChange={e => {
          setSearchQueryValue(e.target.value);
        }}
      />
    </HeaderActions>
  );
}

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

/* Bordered select trigger per docs/design-system.md "Form Fields (Bordered
   Primitives)": border-color-driven states, gold caret, accent fill only when
   a filter is applied (accent = selected, so hover doesn't escalate then). */
const DropdownButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  /* Compact tier per docs/design-system.md "Compact Setting Rows" —
     value text is --text-xs medium. */
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-primary);
  cursor: pointer;
  white-space: nowrap;
  max-width: 200px;
  border: 1px solid
    ${props => (props.$active ? 'var(--accent)' : 'var(--border)')};
  background-color: ${props =>
    props.$active
      ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
      : 'transparent'};
  color: ${props =>
    props.$active ? 'var(--accent)' : 'var(--text-secondary)'};
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: ${props =>
      props.$active ? 'var(--accent)' : 'var(--text-muted)'};
  }

  svg {
    flex-shrink: 0;
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Rounded-row menu surface per docs/design-system.md "Dropdown / Popover
   Menus": small container padding + row gap, no divider lines. */
const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 180px;
  /* Hard cap so long story titles ellipsize (rows are nowrap + ellipsis)
     instead of stretching the menu across the page. */
  max-width: 320px;
  max-height: 280px;
  overflow-y: auto;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px;
  z-index: 1000;
  box-shadow: var(--elevation-toast);
`;

const DropdownSearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 30px;
  min-height: 30px;
  flex-shrink: 0;
  padding: 0 10px;
  margin-bottom: 4px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background-color: transparent;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-family: var(--font-primary);
  outline: none;
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus {
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const DropdownCheckItem = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  width: 100%;
  min-height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

const Checkbox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid
    ${props => (props.$checked ? 'var(--accent)' : 'var(--border)')};
  background-color: ${props =>
    props.$checked ? 'var(--accent)' : 'transparent'};
  flex-shrink: 0;
  transition: all var(--transition-fast) ease;
`;

const DropdownItemLabel = styled.span<{ $type: ContentType }>`
  font-size: var(--text-xs);
  color: ${props => contentTypeColors[props.$type].text};
`;

/* Neutral label for check rows without a per-type color (Clip Status). */
const CheckItemLabel = styled.span`
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

const DropdownSelectItem = styled.button<{ $active: boolean }>`
  /* block (not flex) so text-overflow can ellipsize long story titles;
     padding + line-height reproduce the compact 30px row. flex-shrink: 0
     stops the menu's flex column from squashing rows to fit max-height —
     the list scrolls instead. */
  display: block;
  flex-shrink: 0;
  width: 100%;
  padding: 8px 10px;
  line-height: 1.2;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${props =>
    props.$active ? 'var(--accent)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-family: var(--font-primary);
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover {
    background-color: var(--surface-elevated);
    color: ${props => (props.$active ? 'var(--accent)' : 'var(--foreground)')};
  }
`;

/* Rows that carry an avatar (speakers, Updated By people) need flex layout;
   the name span takes over the ellipsis duty (min-width: 0 lets it shrink
   inside the flex row). */
const AvatarSelectItem = styled(DropdownSelectItem)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
`;

const AvatarRowName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClearFiltersButton = styled.button<{ $visible: boolean }>`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: color var(--transition-fast) ease;
  /* Hidden, not unmounted — it must keep holding its width. The label is
     constant, so that width never changes either. */
  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};

  &:hover:not(:disabled) {
    color: var(--text-secondary);
  }
`;

const SearchInput = styled.input`
  box-sizing: border-box;
  height: 28px;
  padding: 0 10px;
  background-color: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-family: var(--font-primary);
  width: 240px;
  outline: none;
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }

  &:hover {
    border-color: var(--text-muted);
  }

  /* Neutral gray focus per docs/design-system.md — never green or gold. */
  &:focus {
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;
